import Logger from '../utilities/logger';
import A11yDialog from 'a11y-dialog';
import A11yConsentApi from '../a11y-consent-api';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import AbstractComponent from './abstract-component';

/**
 * Base for a modal window.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export default class Modal extends AbstractComponent {
  // Contains all interactable components of the modal. It is important to use this additional wrapper when querying for
  // elements, as nested dialogs may have the same selectors.
  protected readonly document: HTMLElement;

  // Instance of the dialog component
  protected readonly dialog: A11yDialog;

  constructor(api: A11yConsentApi, container: HTMLElement) {
    super(api, container);
    Logger.debug('modal', 'Starting to construct.');

    const document = this.container.querySelector<HTMLElement>(':scope > [role="document"]');
    if (!document) {
      throw new Error('Document container missing.');
    }
    this.document = document;
    this.dialog = new A11yDialog(this.container);

    // Determine scroll target
    const scrollTarget = document.querySelector<HTMLElement>('[data-a11y-consent-scroll]') ?? document;

    this.dialog.on('show', () => {
      Logger.info('modal', 'Show.');
      this.emitter.emit('show', this);
      disableBodyScroll(scrollTarget);
    });

    this.dialog.on('hide', () => {
      Logger.info('modal', 'Hide.');
      this.emitter.emit('hide', this);
      enableBodyScroll(scrollTarget);
    });

    Logger.debug('modal', 'Finished constructing.');
  }

  /**
   * Shows the modal. Basically a shortcut for calling `show()` of the `a11y-dialog` dialog.
   */
  public show(): void {
    this.dialog.show();
  }

  /**
   * Hides the modal. Basically a shortcut for calling `hide()` of the `a11y-dialog` dialog.
   */
  public hide(): void {
    this.dialog.hide();
  }
}
